import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PosthogService } from './posthog.service';
import { UI_VERSION, UiVersion } from '../ui-version';

/** Cookie read by the back end to pick the build it serves. */
const COOKIE_NAME = 'ox_ui';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * The explicit choice found in a `document.cookie` string, wherever the cookie
 * sits among the others.
 */
export function readUiChoice(cookies: string): UiVersion | null {
    const match = cookies.match(/(?:^|;\s*)ox_ui=(next|legacy)(?:;|$)/);
    return (match?.[1] as UiVersion) ?? null;
}

/** PostHog flags driving the roll-out of the new front end. */
const FLAG_OPT_IN = 'new-ui-optin';
const FLAG_DEFAULT = 'new-ui-default';

/**
 * Switches the browser between the legacy and the new front end.
 *
 * The choice is a plain cookie: absent, the back end serves its configured
 * default; `next` or `legacy` is an explicit choice that always wins over the
 * roll-out. Both builds share the origin, so the session survives the switch.
 */
@Injectable({
    providedIn: 'root',
})
export class UiVersionService {
    /** The build currently running. */
    readonly current: UiVersion = UI_VERSION;

    /** Whether this user is invited to try the new front end. */
    readonly canTryNext$ = new BehaviorSubject<boolean>(false);

    constructor(private posthog: PosthogService) {}

    /** The explicit choice stored on this browser, if any. */
    get chosen(): UiVersion | null {
        return readUiChoice(document.cookie);
    }

    /**
     * Follows the roll-out flags once PostHog knows the user: shows the
     * invitation, and moves browsers that never chose a version to the new
     * front end when it became the default for them.
     */
    watchRollout() {
        this.posthog.onFeatureFlags((flags) => {
            this.canTryNext$.next(flags.includes(FLAG_OPT_IN));

            if (flags.includes(FLAG_DEFAULT) && !this.chosen) {
                this.switchTo('next', 'rollout');
            }
        });
    }

    /** Stores the choice and reloads: the back end serves the other build. */
    switchTo(version: UiVersion, reason: 'user' | 'rollout' = 'user') {
        if (version === this.current) {
            return;
        }

        this.posthog.capture('ui_switched', {
            from: this.current,
            to: version,
            reason,
        });
        this.setCookie(version);

        // Leave the analytics call a moment to go out before the page unloads.
        setTimeout(() => window.location.reload(), 150);
    }

    private setCookie(version: UiVersion) {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie =
            `${COOKIE_NAME}=${version}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax` +
            secure;
    }
}
